import { useState } from 'react';
import { Input } from '@/components/ui/Input';
import { ErrorState, LoadingState } from '@/components/ui/States';
import { useKpi } from './hooks';
import { DISPOSITION_LABEL, Section, minutesLabel } from './ui';
import type { Disposition, KpiMetrics } from './types';

const iso = (d: Date) => d.toISOString().slice(0, 10);

/**
 * ED KPIs (D9) — computed by the server from the encounter's own timestamp
 * columns: door-to-triage, door-to-doctor (and % within the level's target),
 * length of stay, boarding, LWBS %, 72-hour returns. Median and 90th
 * percentile alongside the mean, because ED times are long-tailed.
 */
export default function KpiReportTab() {
  const [from, setFrom] = useState(iso(new Date(Date.now() - 29 * 86400000)));
  const [to, setTo] = useState(iso(new Date()));
  const kpi = useKpi(from, to);

  const m = (v: string | null | undefined) => (v == null ? '—' : minutesLabel(Number(v)));
  const tile = (label: string, value: string, sub?: string) => (
    <div className="rounded-xl border border-border bg-card px-4 py-3">
      <div className="text-[20px] font-bold leading-none text-ink">{value}</div>
      <div className="mt-1 text-[11px] uppercase tracking-wide text-ink-muted">{label}</div>
      {sub && <div className="mt-0.5 text-[11px] text-ink-muted">{sub}</div>}
    </div>
  );
  const timeRow = (label: string, o: KpiMetrics, key: 'door_to_triage' | 'door_to_doctor' | 'length_of_stay' | 'boarding') => (
    <tr className="border-b border-border last:border-b-0">
      <td className="py-1.5">{label}</td>
      <td>{m(o[`${key}_median_min`])}</td><td>{m(o[`${key}_avg_min`])}</td><td>{m(o[`${key}_p90_min`])}</td>
    </tr>
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-2">
        <Input type="date" className="w-[160px]" value={from} onChange={(e) => setFrom(e.target.value)} />
        <span className="pb-2 text-ink-muted">to</span>
        <Input type="date" className="w-[160px]" value={to} onChange={(e) => setTo(e.target.value)} />
      </div>
      {kpi.isLoading ? <LoadingState /> : kpi.isError ? <ErrorState message={(kpi.error as Error).message} /> : kpi.data && (() => {
        const o = kpi.data.overall;
        return (
          <>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
              {tile('Arrivals', String(o.arrivals))}
              {tile('Door → doctor (median)', m(o.door_to_doctor_median_min), o.door_to_doctor_within_target_pct != null ? `${o.door_to_doctor_within_target_pct}% within target` : undefined)}
              {tile('Length of stay (median)', m(o.length_of_stay_median_min))}
              {tile('Boarding (median)', m(o.boarding_median_min), `${o.admitted} admitted`)}
              {tile('LWBS', `${o.lwbs_pct ?? 0}%`, `${o.lwbs} patients`)}
              {tile('72-h returns', `${o.returns_72h_pct ?? 0}%`, `${o.returns_72h} of ${o.departed}`)}
            </div>
            <div className="grid gap-3 lg:grid-cols-2">
              <Section title="Time intervals">
                <table className="w-full text-[12px]">
                  <thead><tr className="border-b border-border text-left text-ink-muted"><th className="py-1">Interval</th><th>Median</th><th>Mean</th><th>90th %ile</th></tr></thead>
                  <tbody>
                    {timeRow('Door → triage', o, 'door_to_triage')}
                    {timeRow('Door → doctor', o, 'door_to_doctor')}
                    {timeRow('Length of stay', o, 'length_of_stay')}
                    {timeRow('Boarding (admissions)', o, 'boarding')}
                  </tbody>
                </table>
              </Section>
              <Section title="Dispositions">
                <ul className="space-y-1 text-[12px]">
                  {kpi.data.by_disposition.map((d) => (
                    <li key={d.disposition} className="flex justify-between"><span>{DISPOSITION_LABEL[d.disposition as Disposition] ?? 'Still in the ED'}</span><b>{d.n}</b></li>
                  ))}
                </ul>
                <div className="mt-2 text-[11px] text-ink-muted">MLC: {o.mlc} · LAMA/DAMA: {o.lama_dama} · Absconded: {o.absconded} · Deaths: {o.deaths} · Brought in dead: {o.brought_in_dead}</div>
              </Section>
              <Section title="By triage level" className="lg:col-span-2">
                <table className="w-full text-[12px]">
                  <thead><tr className="border-b border-border text-left text-ink-muted"><th className="py-1">Level</th><th>Arrivals</th><th>Door→doctor median</th><th>Within target</th><th>LOS median</th><th>LWBS %</th></tr></thead>
                  <tbody>
                    {kpi.data.by_level.map((l) => (
                      <tr key={l.level} className="border-b border-border last:border-b-0">
                        <td className="py-1.5 font-semibold">{l.level}</td><td>{l.arrivals}</td><td>{m(l.door_to_doctor_median_min)}</td>
                        <td>{l.door_to_doctor_measured ? `${Math.round((l.door_to_doctor_within_target / l.door_to_doctor_measured) * 100)}%` : '—'}</td>
                        <td>{m(l.length_of_stay_median_min)}</td><td>{l.lwbs_pct ?? 0}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Section>
            </div>
            <div className="text-[11px] text-ink-muted">{Object.entries(kpi.data.definitions).map(([k, v]) => `${k.replace(/_/g, ' ')}: ${v}`).join(' · ')}</div>
          </>
        );
      })()}
    </div>
  );
}
